import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmpresaService } from '../../core/services/empresa.service';
import { CatalogoService } from '../../core/services/catalogo.service';
import { ImagenService } from '../../core/services/imagen.service';
import { AlertService } from '../../core/services/alert.service';
import { CatalogoItemApi } from '../../core/models/api/auth-api';
import { MetodoPagoApi } from '../../core/models/api/empresa-api';
import { PhotoGalleryEditorComponent, PhotoSlot } from '../../shared/components/photo-gallery-editor.component';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

interface MetodoForm {
    uuid: string | null;
    tipoMetodoPagoId: number;
    codigo: string;
    nombre: string;
    numero: string;
    titular: string;
    instrucciones: string;
    orden: number;
    imagenUuid: string | null;
    fotos: PhotoSlot[];
}

@Component({
    selector: 'app-admin-pagos',
    standalone: true,
    imports: [CommonModule, FormsModule, PhotoGalleryEditorComponent, UiLoadingComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Yape y Plin</h2>
      <p class="text-sm text-gray-400 mt-1">Configura el QR, número y titular que verá el cliente al pagar. El comprobante lo sube el cliente y tú lo apruebas en Pedidos.</p>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <p *ngIf="!loading() && metodos().length === 0" class="bg-white rounded-[2rem] border border-rose-100 px-5 py-8 text-center text-sm text-gray-400">No hay métodos de pago configurados.</p>

    <div *ngIf="!loading()" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form *ngFor="let m of metodos(); let i = index" class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm flex flex-col gap-4" (ngSubmit)="guardar(m, i)">
        <p class="text-lg font-black text-gray-800">{{ m.nombre }}</p>
        <app-photo-gallery-editor
          [photos]="m.fotos"
          (photosChange)="setFotos(i, $event)"
          [multiple]="false"
          title="QR / imagen"
          hint="Se ve primero y se sube al guardar.">
        </app-photo-gallery-editor>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Número</span>
          <input [ngModel]="m.numero" (ngModelChange)="patch(i, { numero: $event })" [name]="'numero'+i" required placeholder="Ej: 987654321" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          <span class="text-[11px] text-gray-400">Solo números, sin espacios. Es el número al que el cliente yapea/plinea.</span>
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Titular</span>
          <input [ngModel]="m.titular" (ngModelChange)="patch(i, { titular: $event })" [name]="'titular'+i" required class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Instrucciones para el cliente</span>
          <textarea [ngModel]="m.instrucciones" (ngModelChange)="patch(i, { instrucciones: $event })" [name]="'inst'+i" rows="3" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200"></textarea>
          <span class="text-[11px] text-gray-400">Se muestra en el paso de pago, debajo del QR.</span>
        </label>
        <button [disabled]="guardando() === i" class="py-3 rounded-2xl text-white font-bold text-sm disabled:opacity-60 cursor-pointer" style="background-color: var(--bg-main);">{{ guardando() === i ? 'Guardando…' : 'Guardar ' + m.nombre }}</button>
      </form>
    </div>
    `
})
export class AdminPagosComponent implements OnInit {
    private empresaService = inject(EmpresaService);
    private catalogoService = inject(CatalogoService);
    private imagenService = inject(ImagenService);
    private alerts = inject(AlertService);

    loading = signal(true);
    guardando = signal<number | null>(null);
    metodos = signal<MetodoForm[]>([]);

    ngOnInit(): void {
        this.catalogoService.listTiposMetodoPago().subscribe({
            next: (tipos) => {
                this.empresaService.listMetodosPago().subscribe({
                    next: (rows) => {
                        this.metodos.set(this.merge(tipos, rows));
                        this.loading.set(false);
                    },
                    error: () => {
                        this.loading.set(false);
                        this.alerts.error('No se pudieron cargar los métodos de pago.');
                    }
                });
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar los tipos de pago.');
            }
        });
    }

    setFotos(index: number, fotos: PhotoSlot[]) {
        this.patch(index, { fotos, imagenUuid: fotos[0]?.imagenUuid ?? null });
    }

    patch(index: number, partial: Partial<MetodoForm>) {
        this.metodos.update((list) => list.map((item, i) => i === index ? { ...item, ...partial } : item));
    }

    guardar(m: MetodoForm, index: number) {
        if (this.guardando() !== null) {
            return;
        }
        if (!m.numero.trim() || !m.titular.trim()) {
            this.alerts.error('Completa el número y el titular.');
            return;
        }
        this.guardando.set(index);
        const pending = m.fotos.filter((f) => !!f.file).map((f) => f.file as File);
        if (pending.length) {
            this.imagenService.upload(pending).subscribe({
                next: (imgs) => this.enviar(m, index, imgs[0]?.uuid ?? m.imagenUuid),
                error: (err) => {
                    this.guardando.set(null);
                    this.alerts.error(err?.message || 'No se pudo subir el QR');
                }
            });
            return;
        }
        this.enviar(m, index, m.fotos[0]?.imagenUuid ?? m.imagenUuid);
    }

    private enviar(m: MetodoForm, index: number, imagenUuid: string | null) {
        const body = {
            tipoMetodoPagoId: m.tipoMetodoPagoId,
            numero: m.numero,
            titular: m.titular,
            instrucciones: m.instrucciones,
            imagenUuid,
            orden: m.orden
        };
        const req$ = m.uuid
            ? this.empresaService.updateMetodoPago(m.uuid, body)
            : this.empresaService.createMetodoPago(body);

        req$.subscribe({
            next: (ok) => {
                this.guardando.set(null);
                if (ok) {
                    this.alerts.ok(`${m.nombre} guardado`);
                    if (!m.uuid) {
                        this.empresaService.listMetodosPago().subscribe({
                            next: (rows) => {
                                const found = rows.find((r) => r.tipoMetodoPagoId === m.tipoMetodoPagoId);
                                if (found) this.patch(index, { uuid: found.uuid, imagenUuid: found.imagenUuid });
                            }
                        });
                    }
                } else {
                    this.alerts.error('No se pudo guardar');
                }
            },
            error: (err) => {
                this.guardando.set(null);
                this.alerts.error(err?.message || 'No se pudo guardar');
            }
        });
    }

    private merge(tipos: CatalogoItemApi[], rows: MetodoPagoApi[]): MetodoForm[] {
        const wanted = tipos.filter((t) => t.codigo === 'YAPE' || t.codigo === 'PLIN');
        const source = wanted.length ? wanted : tipos;
        return source.map((tipo, index) => {
            const row = rows.find((r) => r.tipoMetodoPagoId === tipo.id);
            return {
                uuid: row?.uuid ?? null,
                tipoMetodoPagoId: tipo.id,
                codigo: tipo.codigo,
                nombre: tipo.nombre,
                numero: row?.numero ?? '',
                titular: row?.titular ?? '',
                instrucciones: row?.instrucciones ?? 'Si ya realizaste el pago, sube aquí la captura para acelerar el proceso.',
                orden: row?.orden ?? index + 1,
                imagenUuid: row?.imagenUuid ?? null,
                fotos: row?.urlImagen ? [{ previewUrl: row.urlImagen, imagenUuid: row.imagenUuid, file: null }] : []
            };
        });
    }
}
